import { useState, useEffect } from "react";
import { Card, Steps, Form, Button, Divider, Typography, message, Spin } from "antd";
import { aiClinicalDecisionApi, getClinicalDecisionReport } from "./apis";
import ResponseDisplay from "./responseDisplay";
import StepVitals from "./StepVitals";
import StepLabs from "./StepLabs";
import StepImaging from "./StepImaging";
import StepSymptoms from "./StepSymptoms";

const { Title, Text } = Typography;

const steps = [
  { id: 1, title: "Vitals", icon: "💓" },
  { id: 2, title: "Labs", icon: "🧪" },
  { id: 3, title: "Imaging", icon: "📷" },
  { id: 4, title: "Present symptoms", icon: "🩺" },
];

export default function App({ initialReport }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [apiResponse, setApiResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    // Vitals
    age: "",
    gender: "",
    weight: "",
    ageCategory: "adult",
    pregnancy: "no",
    bp: "",
    hr: "",
    temp: "",
    spo2: "",
    rr: "",
    mentalStatus: "",

    // Labs
    labReports: [],
    labResults: "",

    // Imaging
    imagingFiles: [],

    // Present symptoms
    chiefComplaint: "",
    symptomsDescription: "",
    hpiNotes: "",
  });

  const handleNext = () => {
    const newErrors = {};

    // Validate mandatory fields based on current step
    if (currentStep === 1) {
      if (!formData.age) {
        newErrors.age = "Please enter age";
      }
      if (!formData.gender) {
        newErrors.gender = "Please select gender";
      }
      if (!formData.weight) {
        newErrors.weight = "Please enter weight";
      }
    } else if (currentStep === 4) {
      if (!formData.chiefComplaint) {
        newErrors.chiefComplaint = "Please enter chief complaint";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // clear previous errors when moving forward
    setErrors({});

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const handleSubmit = async () => {
    // Final validation before submit (same required rules)
    const submitErrors = {};
    if (!formData.age) submitErrors.age = "Please enter age";
    if (!formData.gender) submitErrors.gender = "Please select gender";
    if (!formData.weight) submitErrors.weight = "Please enter weight";
    if (!formData.chiefComplaint)
      submitErrors.chiefComplaint = "Please enter chief complaint";

    if (Object.keys(submitErrors).length > 0) {
      setErrors(submitErrors);
      return;
    }

    // Combine vitals fields into a single string
    const vitalsString = `Age Category: ${formData.ageCategory}, Pregnancy: ${formData.pregnancy}, BP: ${formData.bp || 'N/A'}, HR: ${formData.hr || 'N/A'}, Temp: ${formData.temp || 'N/A'}, SpO2: ${formData.spo2 || 'N/A'}, Mental Status: ${formData.mentalStatus || 'N/A'}`;

    // Combine Chief Complaint and Symptoms Description
    const chiefComplaintCombined = `${formData.chiefComplaint || ''}${formData.chiefComplaint && formData.symptomsDescription ? ' - ' : ''}${formData.symptomsDescription || ''}`.trim();

    // Create FormData for multipart form data submission
    const formDataToSubmit = new FormData();

    // Append text fields
    formDataToSubmit.append('age', formData.age);
    formDataToSubmit.append('gender', formData.gender || 'Not specified');
    formDataToSubmit.append('weight', formData.weight);
    formDataToSubmit.append('vitals', vitalsString);
    formDataToSubmit.append('lab_notes', formData.labResults || '');
    formDataToSubmit.append('chief_complaint', chiefComplaintCombined);
    formDataToSubmit.append('additional_notes', formData.hpiNotes || '');

    // Append files (combine labs and imaging under the same "files" key)
    if (formData.labReports && formData.labReports.length > 0) {
      formData.labReports.forEach((file) => {
        formDataToSubmit.append("files", file);
      });
    }
    if (formData.imagingFiles && formData.imagingFiles.length > 0) {
      formData.imagingFiles.forEach((file) => {
        formDataToSubmit.append("files", file);
      });
    }

    setIsLoading(true);
    try {
      const response = await aiClinicalDecisionApi(formDataToSubmit);
      console.log(response);
      setApiResponse(response);
      setShowResponse(true);
    } catch (error) {
      console.error(error);

      const status = error?.response?.status;
      if (status === 401) {
        message.error("You are not authorized! please login");
      } else if (status === 403) {
        message.error("You are not authorized! please verified your email");
      } else {
        // Try to show backend message if available, otherwise generic
        const backendMsg =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message;
        message.error(
          backendMsg ||
          "An error occurred while processing your request. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // clear error for this field on change
    if (errors[field]) {
      const nextErrors = { ...errors };
      delete nextErrors[field];
      setErrors(nextErrors);
    }
    setFormData({ ...formData, [field]: value });
  };

  const handleUploadChange = (field) => ({ fileList }) => {
    const files =
      fileList && fileList.length > 0
        ? fileList
          .map((file) => file.originFileObj)
          .filter((file) => !!file)
        : [];
    setFormData({ ...formData, [field]: files });
  };

  // Load report when initialReport is provided
  useEffect(() => {
    if (initialReport) {
      setIsLoadingReport(true);
      const reportId = initialReport.id;
      getClinicalDecisionReport(reportId)
        .then((response) => {
          // Transform response to match ResponseDisplay expected format
          // ResponseDisplay expects: { physician_report: {...} }
          // API returns: { id, age, gender, ..., report_json: {...} }
          // The report_json contains the actual report data
          const transformedResponse = {
            physician_report: response.report_json || response
          };
          setApiResponse(transformedResponse);
          setShowResponse(true);
        })
        .catch((error) => {
          console.error("Error loading report:", error);
          const status = error?.response?.status;
          if (status === 401) {
            message.error("You are not authorized! Please login");
          } else if (status === 403) {
            message.error("You are not authorized! Please verify your email");
          } else {
            message.error("Failed to load report. Please try again.");
          }
        })
        .finally(() => {
          setIsLoadingReport(false);
        });
    } else {
      // Reset when no initial report
      setShowResponse(false);
      setApiResponse(null);
    }
  }, [initialReport]);



  if (isLoadingReport) {
    return (
      <div className="clinical-decision-assistant-sec">
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="clinical-decision-assistant-sec">

      {!showResponse && (
        <div className="py-6">
          <div className="mb-6">
            <Title level={3} style={{ marginBottom: 4 }}>
              Clinical Decision Assistant
            </Title>
            <Text type="secondary">
              Enter patient information to generate AI-assisted clinical insights.
            </Text>
          </div>

          <Steps
            current={currentStep - 1}
            items={steps.map((step) => ({
              title: step.title,
            }))}
            style={{ marginBottom: 24 }}
          />

          <Divider />

          <Form layout="vertical">
            {currentStep === 1 && (
              <StepVitals
                formData={formData}
                onChangeField={handleInputChange}
                errors={errors}
              />
            )}

            {currentStep === 2 && (
              <StepLabs
                formData={formData}
                onChangeField={handleInputChange}
                onUploadField={handleUploadChange}
              />
            )}

            {currentStep === 3 && (
              <StepImaging
                imagingFiles={formData.imagingFiles}
                onUploadField={handleUploadChange}
              />
            )}

            {currentStep === 4 && (
              <StepSymptoms
                formData={formData}
                onChangeField={handleInputChange}
                errors={errors}
              />
            )}
          </Form>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <Button
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Previous
            </Button>

            {currentStep < steps.length ? (
              <Button
                type="primary"
                style={{ backgroundColor: "#0B7BC1", borderColor: "#0B7BC1" }}
                onClick={handleNext}
              >
                Next
              </Button>
            ) : (
              <Button
                type="primary"
                style={{ backgroundColor: "#0B7BC1", borderColor: "#0B7BC1" }}
                onClick={handleSubmit}
                loading={isLoading}
              >
                Submit Data
              </Button>
            )}
          </div>
          <div>
            {currentStep === steps.length && (
              <div className="py-6 text-center">

                <Text type="secondary">
                  Results may take up to 3 minutes to generate.
                </Text>
              </div>
            )}
          </div>
        </div>

      )}


      {/* Response Display */}
      {showResponse && apiResponse && (
        <div className="py-6 overflow-hidden">
          <ResponseDisplay response={apiResponse} />
          <div className="pt-4">
            <Button
              type="primary"
              block
              style={{
                marginTop: 16,
                backgroundColor: "#0B7BC1",
                borderColor: "#0B7BC1",
                height: 44,
              }}
              onClick={() => {
                setShowResponse(false);
                setApiResponse(null);
                setCurrentStep(1);
                setFormData({
                  age: "",
                  gender: "",
                  weight: "",
                  ageCategory: "adult",
                  pregnancy: "no",
                  bp: "",
                  hr: "",
                  temp: "",
                  spo2: "",
                  rr: "",
                  mentalStatus: "",
                  labReports: [],
                  labResults: "",
                  imagingFiles: [],
                  chiefComplaint: "",
                  symptomsDescription: "",
                  hpiNotes: "",
                });
              }}
            >
              {initialReport ? "Back to History" : "New Analysis"}
            </Button>
          </div>
        </div>
      )}

    </div>
  );
}


